import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Path } from "react-native-svg";

import { ActivityInboxBody, STUDENT_FILTERS } from "../notifications/ActivityInboxBody";
import { Avatar } from "../ui/Avatar";
import { signOutToHome } from "../../lib/auth";
import {
  getResumeSignedUrl,
  pickAvatarFile,
  uploadAvatar,
  uploadResume,
} from "../../lib/profileUploads";
import { getMyStudent, upsertMyStudent } from "../../lib/students";
import { gradYearOptions, majorsForSchools } from "../../lib/majors";
import {
  type IgnoredRequesterRow,
  listMyIgnoredRequesters,
  unignoreTeamRequester,
} from "../../lib/teams";
import {
  COMMON_TIMEZONES,
  getSystemTimeZone,
  getUserTimeZone,
  setUserTimeZone,
} from "../../lib/datetime";
import { buildIcsForDeadlines, downloadIcs } from "../../lib/icsCalendar";
import { listUpcomingDeadlines } from "../../lib/studentDashboard";
import { supabase } from "../../lib/supabase";
import type { Student } from "../../types/database";
import { HeaderOverlay, HeaderOverlayTokens } from "./HeaderOverlay";
import { BrandExit } from "./BrandExit";
import { GlobalSearchOverlay } from "./GlobalSearchOverlay";

const { FOREST, AMBER } = HeaderOverlayTokens;
const SAND_DARK = "#D9D2C5";
const TEXT_MUTED = "rgba(28,43,26,0.62)";
const TEXT_FAINT = "rgba(28,43,26,0.42)";

export type StudentOverlayKey = "activity" | "settings" | "profile" | "search" | null;

export function StudentHeaderOverlays({
  open,
  onClose,
  user,
  avatarName,
  avatarUrl,
}: {
  open: StudentOverlayKey;
  onClose: () => void;
  user: { fullName: string; email: string | null };
  avatarName: string;
  avatarUrl?: string | null;
}) {
  return (
    <>
      <HeaderOverlay
        visible={open === "activity"}
        onClose={onClose}
        title="Activity"
        subtitle="Announcements, deliverables, team updates"
      >
        <ActivityInboxBody theme="light" filters={STUDENT_FILTERS} hideHero />
      </HeaderOverlay>

      <HeaderOverlay
        visible={open === "settings"}
        onClose={onClose}
        title="Settings"
        subtitle="Configure how Coravu works for you."
      >
        <SettingsOverlayBody email={user.email} />
      </HeaderOverlay>

      <HeaderOverlay
        visible={open === "profile"}
        onClose={onClose}
        title="Your profile"
        subtitle={user.email ?? undefined}
      >
        <ProfileOverlayBody
          fullName={user.fullName}
          email={user.email}
          avatarName={avatarName}
          avatarUrl={avatarUrl}
        />
      </HeaderOverlay>

      {/* Global search palette — Cmd/Ctrl+K opens this from anywhere
          inside the student portal. Lives at the same layer as the
          activity / settings / profile overlays so its modal presents
          on top of any underlying page content. */}
      <GlobalSearchOverlay visible={open === "search"} onClose={onClose} />
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Settings overlay body
// ────────────────────────────────────────────────────────────

function SettingsOverlayBody({ email }: { email: string | null }) {
  const router = useRouter();

  // Notification preferences are placeholder toggles for now —
  // they store locally so the UI demonstrates the surface without
  // a backend column. A real preferences table is a follow-up.
  const [emailDigest, setEmailDigest] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [marketingEmails, setMarketingEmails] = useState(false);
  // When the user hits Sign out we play the BrandExit animation first,
  // THEN call signOutToHome on completion so the leave is brand-aligned.
  const [exiting, setExiting] = useState(false);

  return (
    <View style={{ padding: 28, gap: 28 }}>
      {/* Account section */}
      <SectionBlock label="Account">
        <Row label="Email" value={email ?? "—"} readOnly />
        <Pressable
          onPress={async () => {
            if (email) {
              const { error } = await supabase.auth.resetPasswordForEmail(email);
              if (!error) {
                await Linking.openURL("mailto:");
              }
            }
          }}
          style={({ hovered }) => [
            {
              alignSelf: "flex-start",
              paddingVertical: 8,
              paddingHorizontal: 12,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: "rgba(28,43,26,0.12)",
              marginTop: 4,
            },
            Platform.OS === "web" && hovered ? { borderColor: AMBER } : {},
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {},
          ]}
        >
          <Text
            style={{
              fontFamily: "DMSans_600SemiBold",
              fontSize: 10.5,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: TEXT_MUTED,
            }}
          >
            Send password reset email
          </Text>
        </Pressable>
      </SectionBlock>

      {/* Notifications */}
      <SectionBlock label="Notifications">
        <ToggleRow
          label="Daily email digest"
          hint="One email each morning summarizing what changed."
          value={emailDigest}
          onChange={setEmailDigest}
        />
        <ToggleRow
          label="Push notifications"
          hint="Real-time updates on the web app."
          value={pushEnabled}
          onChange={setPushEnabled}
        />
        <ToggleRow
          label="Product announcements"
          hint="Hear about new Coravu features and partner programs."
          value={marketingEmails}
          onChange={setMarketingEmails}
        />
        <Text
          style={{
            marginTop: 4,
            fontFamily: "DMSans_400Regular",
            fontSize: 11,
            color: TEXT_FAINT,
          }}
        >
          Notification preferences sync across devices once we wire them to your account — for now they\'re saved on this device.
        </Text>
      </SectionBlock>

      <SectionBlock label="Time zone">
        <TimezoneSelect />
      </SectionBlock>

      <SectionBlock label="Calendar">
        <CalendarSync />
      </SectionBlock>

      {/* Ignored requesters — captain-side blocklist. Only renders if the
          user has anything to unignore. Saves the rest of users from a
          dead-empty section. */}
      <IgnoredRequestersSection />

      {/* Sign out */}
      <SectionBlock label="Session">
        <Pressable
          onPress={() => {
            setExiting(true);
          }}
          style={({ hovered }) => [
            {
              alignSelf: "flex-start",
              paddingVertical: 11,
              paddingHorizontal: 22,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: "rgba(28,43,26,0.20)",
              backgroundColor: "transparent",
            },
            Platform.OS === "web" && hovered
              ? { borderColor: AMBER, backgroundColor: "rgba(181,135,58,0.06)" }
              : {},
            Platform.OS === "web"
              ? ({ cursor: "pointer", transition: "border-color 0.18s ease, background-color 0.18s ease" } as const)
              : {},
          ]}
        >
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: FOREST,
            }}
          >
            Sign out
          </Text>
        </Pressable>
      </SectionBlock>

      {exiting ? (
        <BrandExit
          onComplete={() => {
            void signOutToHome(router);
          }}
        />
      ) : null}
    </View>
  );
}

function TimezoneSelect() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<string>(() => getUserTimeZone());
  const [anchor, setAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const triggerRef = useRef<View | null>(null);
  const systemTz = getSystemTimeZone();
  const isWeb = Platform.OS === "web";
  const { height: viewportH } = useWindowDimensions();

  // Decide placement: open below the trigger by default, but flip upward
  // when there\'s more space above. Also cap the menu height to the
  // available space (with a small safety inset) so it never spills off
  // the viewport into the OS taskbar.
  const placement = (() => {
    if (!anchor) return null;
    const margin = 16;
    const spaceBelow = Math.max(0, viewportH - (anchor.y + anchor.h) - margin);
    const spaceAbove = Math.max(0, anchor.y - margin);
    const flipUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxH = Math.min(360, flipUp ? spaceAbove : spaceBelow);
    return { flipUp, maxH };
  })();

  const labelOf = (tz: string) => {
    const found = COMMON_TIMEZONES.find((o) => o.value === tz);
    return found?.label ?? tz;
  };

  const measure = useCallback(() => {
    const node = triggerRef.current as unknown as { measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void } | null;
    if (!node || typeof node.measureInWindow !== "function") return;
    node.measureInWindow((x, y, w, h) => {
      setAnchor({ x, y, w, h });
    });
  }, []);

  const openDropdown = () => {
    measure();
    setOpen(true);
  };

  return (
    <View>
      <View ref={(r) => { triggerRef.current = r; }} onLayout={measure}>
      <Pressable
        onPress={() => (open ? setOpen(false) : openDropdown())}
        style={({ hovered }) => [
          {
            flexDirection: "row",
            alignItems: "center",
            gap: 14,
            backgroundColor: COLORS.WHITE.hex,
            borderWidth: 1,
            borderColor: SAND_DARK,
            borderRadius: 4,
            paddingVertical: 10,
            paddingHorizontal: 14,
          },
          isWeb
            ? ({
                cursor: "pointer",
                transition: "border-color 0.18s ease, background-color 0.18s ease",
              } as const)
            : null,
          isWeb && (hovered || open)
            ? { borderColor: "rgba(181,135,58,0.45)" }
            : null,
        ]}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 8.5,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: AMBER,
              marginBottom: 2,
            }}
          >
            Display dates in
          </Text>
          <Text
            style={{
              fontFamily: "DMSans_600SemiBold",
              fontSize: 13,
              color: FOREST,
            }}
            numberOfLines={1}
          >
            {labelOf(current)}
          </Text>
        </View>
        <View
          style={[
            { transform: [{ rotate: open ? "180deg" : "0deg" }] },
            isWeb ? ({ transition: "transform 0.18s ease" } as object) : null,
          ]}
        >
          <Svg width={10} height={10} viewBox="0 0 10 10" fill="none">
            <Path
              d="M2 3.5l3 3 3-3"
              stroke={TEXT_MUTED}
              strokeWidth={1.4}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
        </View>
      </Pressable>

      </View>

      <RNModal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          onPress={() => setOpen(false)}
          style={{ flex: 1, backgroundColor: "transparent" }}
        >
          {anchor && placement ? (
            <Pressable
              onPress={(e) => e.stopPropagation()}
              style={[
                {
                  position: "absolute",
                  left: anchor.x,
                  width: anchor.w,
                  backgroundColor: COLORS.WHITE.hex,
                  borderWidth: 1,
                  borderColor: SAND_DARK,
                  borderRadius: 4,
                  paddingVertical: 4,
                  maxHeight: placement.maxH,
                },
                placement.flipUp
                  ? { bottom: viewportH - anchor.y + 6 }
                  : { top: anchor.y + anchor.h + 6 },
                isWeb
                  ? ({ boxShadow: "0 22px 44px rgba(28,43,26,0.18)" } as object)
                  : null,
              ]}
            >
              <ScrollView style={{ maxHeight: Math.max(0, placement.maxH - 8) }}>
            {/* "Use system default" first, then the curated list. */}
            <TimezoneOption
              label={`System default (${labelOf(systemTz)})`}
              selected={current === systemTz}
              onPress={() => {
                setUserTimeZone(null);
                setCurrent(getUserTimeZone());
                setOpen(false);
              }}
            />
            <View style={{ height: 1, backgroundColor: "rgba(28,43,26,0.06)", marginVertical: 4 }} />
            {COMMON_TIMEZONES.map((opt) => (
              <TimezoneOption
                key={opt.value}
                label={opt.label}
                selected={current === opt.value}
                onPress={() => {
                  setUserTimeZone(opt.value);
                  setCurrent(opt.value);
                  setOpen(false);
                }}
              />
            ))}
              </ScrollView>
            </Pressable>
          ) : null}
        </Pressable>
      </RNModal>

      <Text
        style={{
          marginTop: 8,
          fontFamily: "DMSans_400Regular",
          fontSize: 11,
          color: TEXT_FAINT,
        }}
      >
        All due dates and deliverable times across the platform render in this zone — no abbreviation suffix.
      </Text>
    </View>
  );
}

function TimezoneOption({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          paddingVertical: 9,
          paddingHorizontal: 14,
          backgroundColor: selected ? "rgba(181,135,58,0.08)" : "transparent",
        },
        isWeb && hovered && !selected
          ? { backgroundColor: "rgba(28,43,26,0.04)" }
          : null,
        isWeb ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View
        style={{
          width: 10,
          height: 10,
          borderRadius: 5,
          borderWidth: 1.2,
          borderColor: selected ? AMBER : "rgba(28,43,26,0.32)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {selected ? (
          <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: AMBER }} />
        ) : null}
      </View>
      <Text
        style={{
          fontFamily: selected ? "DMSans_600SemiBold" : "DMSans_500Medium",
          fontSize: 12.5,
          color: selected ? AMBER : FOREST,
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function CalendarSync() {
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const isWeb = Platform.OS === "web";

  const onDownload = async () => {
    setBusy(true);
    setErrorMsg(null);
    try {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      if (!uid) {
        setErrorMsg("Sign in required.");
        return;
      }
      // Pull a generous window so the download is genuinely useful — up
      // to 100 upcoming deliverables.
      const rows = await listUpcomingDeadlines(uid, 100);
      if (rows.length === 0) {
        setErrorMsg("No upcoming deliverables to export yet.");
        setCount(0);
        return;
      }
      const ics = buildIcsForDeadlines(rows);
      downloadIcs("coravu-deliverables.ics", ics);
      setCount(rows.length);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Could not generate calendar file.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={{ gap: 12 }}>
      <Text
        style={{
          fontFamily: "DMSans_400Regular",
          fontSize: 12.5,
          lineHeight: 18,
          color: TEXT_MUTED,
        }}
      >
        Add your deliverable due dates to Google Calendar, Outlook, or Apple Calendar. Download the file once; then in your calendar app choose <Text style={{ fontWeight: "600" }}>Import</Text> and pick the file.
      </Text>

      <Pressable
        onPress={() => void onDownload()}
        disabled={busy}
        style={({ hovered }) => [
          {
            alignSelf: "flex-start",
            paddingVertical: 11,
            paddingHorizontal: 22,
            borderRadius: 4,
            backgroundColor: busy ? "rgba(28,43,26,0.18)" : AMBER,
          },
          isWeb && hovered && !busy ? { backgroundColor: COLORS.AMBER_WARM.hex } : null,
          // `cursor: not-allowed` + `transition` are RN-Web extensions
          // not modeled by RN's CursorValue / ViewStyle types. Cast
          // through unknown to bypass the type check while preserving
          // the runtime CSS.
          isWeb ? ({ cursor: busy ? "not-allowed" : "pointer", transition: "background-color 0.18s ease" } as unknown as ViewStyle) : null,
        ]}
      >
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: busy ? "rgba(255,255,255,0.7)" : COLORS.BLACK.hex,
          }}
        >
          {busy ? "Generating\u2026" : "Download .ics file"}
        </Text>
      </Pressable>

      {count != null && count > 0 ? (
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 11,
            color: AMBER,
          }}
        >
          Exported {count} upcoming {count === 1 ? "deliverable" : "deliverables"} \u2014 import the file in your calendar app.
        </Text>
      ) : null}

      {errorMsg ? (
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 11,
            color: "#C0392B",
          }}
        >
          {errorMsg}
        </Text>
      ) : null}

      <View
        style={{
          marginTop: 6,
          padding: 14,
          borderRadius: 4,
          backgroundColor: "rgba(181,135,58,0.06)",
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.22)",
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 9,
            letterSpacing: 1.6,
            textTransform: "uppercase",
            color: AMBER,
            marginBottom: 6,
          }}
        >
          Auto-sync \u00B7 Coming soon
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 11.5,
            lineHeight: 17,
            color: TEXT_MUTED,
          }}
        >
          Soon you\u2019ll get a personal subscription URL to paste into Google Calendar or Outlook. Your calendar then auto-refreshes whenever a Coravu deliverable is added or removed \u2014 one-way, no edits flow back into Coravu.
        </Text>
      </View>
    </View>
  );
}

function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: "rgba(28,43,26,0.06)",
      }}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontFamily: "DMSans_600SemiBold",
            fontSize: 13,
            color: FOREST,
          }}
        >
          {label}
        </Text>
        {hint ? (
          <Text
            style={{
              marginTop: 2,
              fontFamily: "DMSans_400Regular",
              fontSize: 12,
              color: TEXT_MUTED,
              lineHeight: 17,
            }}
          >
            {hint}
          </Text>
        ) : null}
      </View>
      <BrandToggle value={value} onChange={onChange} />
    </View>
  );
}

function Row({
  label,
  value,
  readOnly,
}: {
  label: string;
  value: string;
  readOnly?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        paddingVertical: 8,
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 12,
          color: TEXT_MUTED,
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontFamily: "DMSans_500Medium",
          fontSize: 13,
          color: readOnly ? TEXT_MUTED : FOREST,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function IgnoredRequestersSection() {
  const [rows, setRows] = useState<IgnoredRequesterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const r = await listMyIgnoredRequesters();
      if (alive) {
        setRows(r);
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const onUnignore = useCallback(async (teamId: string, studentId: string) => {
    const key = teamId + ":" + studentId;
    setBusyKey(key);
    const { error } = await unignoreTeamRequester(teamId, studentId);
    setBusyKey(null);
    if (error) return;
    setRows((prev) =>
      prev.filter((r) => !(r.team_id === teamId && r.student_id === studentId)),
    );
  }, []);

  if (loading || rows.length === 0) return null;

  return (
    <SectionBlock label="Ignored requesters">
      <Text
        style={{
          marginBottom: 8,
          fontFamily: "DMSans_400Regular",
          fontSize: 12,
          color: TEXT_MUTED,
          lineHeight: 17,
        }}
      >
        People you have blocked from re-requesting to join your team. Unignore to let them request again.
      </Text>
      <View style={{ gap: 8 }}>
        {rows.map((r) => {
          const key = r.team_id + ":" + r.student_id;
          const busy = busyKey === key;
          const displayName = r.student_name ?? r.student_id.slice(0, 8);
          return (
            <View
              key={key}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                paddingVertical: 8,
                paddingHorizontal: 12,
                borderRadius: 6,
                borderWidth: 0.5,
                borderColor: SAND_DARK,
                backgroundColor: COLORS.WHITE.hex,
              }}
            >
              <Avatar name={displayName} imageUrl={r.student_avatar} size="sm" />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{
                    fontFamily: "DMSans_600SemiBold",
                    fontSize: 13,
                    color: FOREST,
                  }}
                  numberOfLines={1}
                >
                  {displayName}
                </Text>
                <Text
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 11,
                    color: TEXT_MUTED,
                  }}
                  numberOfLines={1}
                >
                  {r.team_name + " \u00b7 " + r.competition_name}
                </Text>
              </View>
              <Pressable
                onPress={() => void onUnignore(r.team_id, r.student_id)}
                disabled={busy}
                style={({ hovered }) => [
                  {
                    paddingHorizontal: 12,
                    paddingVertical: 7,
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor:
                      hovered && Platform.OS === "web" ? AMBER : "rgba(28,43,26,0.20)",
                    backgroundColor:
                      hovered && Platform.OS === "web"
                        ? "rgba(181,135,58,0.06)"
                        : "transparent",
                  },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: "DMSans_700Bold",
                    fontSize: 10.5,
                    letterSpacing: 1.2,
                    color: TEXT_MUTED,
                  }}
                >
                  {busy ? "\u2026" : "UNIGNORE"}
                </Text>
              </Pressable>
            </View>
          );
        })}
      </View>
    </SectionBlock>
  );
}

function SectionBlock({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 9.5,
          letterSpacing: 1.8,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 12,
        }}
      >
        {label}
      </Text>
      <View style={{ gap: 4 }}>{children}</View>
    </View>
  );
}

// ────────────────────────────────────────────────────────────
// Profile overlay body — inline editor
// ────────────────────────────────────────────────────────────

const PROGRAM_LEVELS = ["Undergraduate", "Graduate", "MBA", "Doctoral"] as const;
const GRAD_TERMS = ["Spring", "Summer", "Fall"] as const;

// LinkedIn URL validation. Accepts http(s)://, optional www. or m. prefix,
// linkedin.com domain, and at least some path beyond the slash (so we
// reject the bare domain). normalizeLinkedinUrl returns the cleaned URL
// (with https:// prefix) when valid, or null when not — drives both the
// inline error and the save-time guard so junk like "jsdjlkfjsdlfkjf"
// can never land in the database.
const LINKEDIN_RE = /^(?:https?:\/\/)?(?:www\.|m\.)?linkedin\.com\/[A-Za-z0-9._~%\-/?#=&+]+$/i;

function normalizeLinkedinUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (!LINKEDIN_RE.test(trimmed)) return null;
  // Prepend https:// if the user pasted a bare linkedin.com/...
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return "https://" + trimmed.replace(/^\/+/, "");
}

function isLinkedinUrlValid(input: string): boolean {
  const trimmed = input.trim();
  if (!trimmed) return true; // empty is fine — field is optional
  return normalizeLinkedinUrl(trimmed) != null;
}

function ProfileOverlayBody({
  fullName: initialFullName,
  email,
  avatarName,
  avatarUrl: initialAvatarUrl,
}: {
  fullName: string;
  email: string | null;
  avatarName: string;
  avatarUrl?: string | null;
}) {
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);

  // Editable fields
  const [fullName, setFullName] = useState(initialFullName);
  const [bio, setBio] = useState("");
  const [university, setUniversity] = useState("");
  const [major, setMajor] = useState("");
  const [programLevel, setProgramLevel] = useState<string>("");
  const [gradTerm, setGradTerm] = useState<string>("");
  const [gradYear, setGradYear] = useState<string>("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialAvatarUrl ?? null);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);

  // Save state
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Avatar / resume upload state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [resumeSignedUrl, setResumeSignedUrl] = useState<string | null>(null);
  const baselineRef = useRef<string>("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, prof] = await Promise.all([
        getMyStudent(),
        supabase.auth.getUser().then(async (res) => {
          const uid = res.data.user?.id;
          if (!uid) return null;
          const { data } = await supabase
            .from("user_profiles")
            .select("full_name, avatar_url")
            .eq("id", uid)
            .maybeSingle();
          return data;
        }),
      ]);
      setStudent(s);
      if (s) {
        setBio(s.bio ?? "");
        setUniversity(s.university ?? "");
        setMajor(s.major ?? "");
        setProgramLevel(s.program_level ?? "");
        setGradTerm(s.graduation_term ?? "");
        setGradYear(s.graduation_year ? String(s.graduation_year) : "");
        setLinkedinUrl(s.linkedin_url ?? "");
        setPhone(s.phone ?? "");
        setResumeUrl(s.resume_url ?? null);
        if (s.resume_url) {
          const signed = await getResumeSignedUrl(s.resume_url);
          setResumeSignedUrl(signed);
        }
      }
      if (prof?.full_name) setFullName(prof.full_name);
      if (prof?.avatar_url) setAvatarUrl(prof.avatar_url);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Capture the loaded form state as the autosave baseline once the
  // initial load finishes. Without this the empty useState defaults
  // would look "dirty" against any populated row and the autosave
  // would fire immediately on mount.
  useEffect(() => {
    if (loading) return;
    if (baselineRef.current) return;
    baselineRef.current = JSON.stringify({
      fullName,
      bio,
      university,
      major,
      programLevel,
      gradTerm,
      gradYear,
      linkedinUrl,
      phone,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const save = useCallback(async () => {
    setSaving(true);
    setErrorMsg(null);
    // Reject obviously-bad LinkedIn input here so the autosave doesn\'t
    // wipe a previously-saved valid URL with garbage. Empty strings pass
    // (clearing the field is fine).
    if (linkedinUrl.trim() && !isLinkedinUrlValid(linkedinUrl)) {
      setErrorMsg("Enter a real LinkedIn URL (e.g. https://www.linkedin.com/in/yourname).");
      setSaving(false);
      return;
    }
    try {
      const yearNum = gradYear ? Number(gradYear) : null;
      // university stays out of this patch — it's locked in the UI
      // (sourced from signup) so a self-service edit can't push someone
      // into a program whose school-eligibility filtered them out.
      // major IS editable, but only via the dropdown of majors offered
      // at the user's locked university — see MajorPickerField.
      const { error: studentErr } = await upsertMyStudent({
        bio: bio.trim() || null,
        major: major.trim() || null,
        program_level: programLevel || null,
        graduation_term: gradTerm || null,
        graduation_year: yearNum && Number.isFinite(yearNum) ? yearNum : null,
        linkedin_url: normalizeLinkedinUrl(linkedinUrl),
        phone: phone.trim() || null,
      });
      if (studentErr) throw studentErr;

      const { data: u } = await supabase.auth.getUser();
      if (u.user) {
        // user_profiles.email has a NOT NULL constraint. The first save
        // for a brand-new account triggers the INSERT branch of upsert,
        // so include email even though it never changes after that.
        const { error: profErr } = await supabase
          .from("user_profiles")
          .upsert(
            {
              id: u.user.id,
              email: u.user.email ?? null,
              full_name: fullName.trim() || null,
            },
            { onConflict: "id" }
          );
        if (profErr) throw profErr;
      }

      setSavedAt(new Date());
      baselineRef.current = JSON.stringify({
        fullName,
        bio,
        university,
        major,
        programLevel,
        gradTerm,
        gradYear,
        linkedinUrl,
        phone,
      });
    } catch (err) {
      // Postgres / Supabase errors don't always extend Error; pull a
      // message off whatever shape we got so the user sees something
      // useful instead of the fallback.
      let msg: string | null = null;
      if (err instanceof Error) msg = err.message;
      else if (err && typeof err === "object") {
        const e = err as { message?: unknown; details?: unknown; hint?: unknown };
        if (typeof e.message === "string") msg = e.message;
        else if (typeof e.details === "string") msg = e.details;
        else if (typeof e.hint === "string") msg = e.hint;
      }
      setErrorMsg(msg ?? "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  }, [
    fullName,
    bio,
    university,
    major,
    programLevel,
    gradTerm,
    gradYear,
    linkedinUrl,
    phone,
  ]);

  // Debounced autosave: ~1.2s after edits stop, if the form is dirty
  // against the last-saved baseline, fire save(). Skips while loading
  // or while a save is already running.
  useEffect(() => {
    if (loading || saving) return;
    if (!baselineRef.current) return;
    const snapshot = JSON.stringify({
      fullName,
      bio,
      university,
      major,
      programLevel,
      gradTerm,
      gradYear,
      linkedinUrl,
      phone,
    });
    if (snapshot === baselineRef.current) return;
    const timer = setTimeout(() => {
      void save();
    }, 1200);
    return () => clearTimeout(timer);
  }, [
    fullName,
    bio,
    university,
    major,
    programLevel,
    gradTerm,
    gradYear,
    linkedinUrl,
    phone,
    loading,
    saving,
    save,
  ]);

  const onPickAvatar = useCallback(async () => {
    setUploadingAvatar(true);
    setErrorMsg(null);
    try {
      const file = await pickAvatarFile();
      if (!file) return;
      const url = await uploadAvatar(file);
      setAvatarUrl(url);
      const { data: u } = await supabase.auth.getUser();
      if (u.user) {
        // Same NOT NULL guard as the main save path.
        await supabase
          .from("user_profiles")
          .upsert(
            { id: u.user.id, email: u.user.email ?? null, avatar_url: url },
            { onConflict: "id" }
          );
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Could not upload photo.");
    } finally {
      setUploadingAvatar(false);
    }
  }, []);

  const onPickResume = useCallback(async () => {
    // Lazy-load expo-document-picker to avoid an unused import on web.
    setUploadingResume(true);
    setErrorMsg(null);
    try {
      const DocumentPicker = await import("expo-document-picker");
      const res = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
        multiple: false,
        copyToCacheDirectory: true,
      });
      if (res.canceled || !res.assets || res.assets.length === 0) return;
      const path = await uploadResume(res.assets[0]);
      setResumeUrl(path);
      const signed = await getResumeSignedUrl(path);
      setResumeSignedUrl(signed);
      await upsertMyStudent({ resume_url: path });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Could not upload resume.");
    } finally {
      setUploadingResume(false);
    }
  }, []);

  // IMPORTANT: every hook above must run on every render. The `loading`
  // early-return below must come AFTER all hooks, otherwise React throws
  // "Rendered fewer hooks than expected" when the loading state flips.
  // Form autosaves ~1.2s after edits stop.
  const savedLabel = useMemo(() => {
    if (saving) return "Saving\u2026";
    if (!savedAt) return "Changes save automatically";
    return `Auto-saved at ${savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  }, [saving, savedAt]);

  if (loading) {
    return (
      <View style={{ padding: 32, alignItems: "center", justifyContent: "center", flex: 1 }}>
        <ActivityIndicator size="small" color={FOREST} />
      </View>
    );
  }

  const initials = avatarName;

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: 28, paddingBottom: 64, gap: 28 }}
      keyboardShouldPersistTaps="handled"
    >
      {/* Identity row */}
      <View style={{ flexDirection: "row", alignItems: "center", gap: 18 }}>
        <Avatar name={initials} imageUrl={avatarUrl} size="lg" theme="light" />
        <View style={{ flex: 1, minWidth: 0 }}>
          <TextInput
            value={fullName}
            onChangeText={setFullName}
            placeholder="Full name"
            placeholderTextColor={TEXT_FAINT}
            style={{
              fontFamily: "CormorantGaramond_500Medium",
              fontSize: 28,
              color: FOREST,
              borderBottomWidth: 1,
              borderBottomColor: "rgba(28,43,26,0.12)",
              paddingVertical: 6,
              outlineWidth: 0 as unknown as number,
            }}
          />
          {email ? (
            <Text
              style={{
                marginTop: 4,
                fontFamily: "DMSans_400Regular",
                fontSize: 12,
                color: TEXT_MUTED,
              }}
              numberOfLines={1}
            >
              {email}
            </Text>
          ) : null}
          <View style={{ flexDirection: "row", gap: 10, marginTop: 10 }}>
            <UploadButton
              label={uploadingAvatar ? "Uploading…" : "Change photo"}
              onPress={onPickAvatar}
              disabled={uploadingAvatar}
            />
          </View>
        </View>
      </View>

      {/* Bio */}
      <Field label="About you">
        <TextInput
          value={bio}
          onChangeText={setBio}
          placeholder="Two or three sentences — what you study, what you\'re into, what you want to build."
          placeholderTextColor={TEXT_FAINT}
          multiline
          numberOfLines={4}
          style={{
            minHeight: 96,
            backgroundColor: COLORS.WHITE.hex,
            borderWidth: 1,
            borderColor: SAND_DARK,
            borderRadius: 4,
            padding: 12,
            fontFamily: "DMSans_400Regular",
            fontSize: 13,
            color: FOREST,
            outlineWidth: 0 as unknown as number,
            textAlignVertical: "top",
          }}
        />
      </Field>

      {/* Education */}
      <Field label="Education">
        {/* School-verification callout. Shows when the student's school
            hasn't been auto-verified via email domain — they've been
            picked up by manual admin onboarding, or they're self-
            attesting. Cases that flip `requires_verified_school = true`
            block joins until this clears. */}
        {student && student.school_verified === false ? (
          <View
            style={{
              padding: 12,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.45)",
              backgroundColor: "rgba(181,135,58,0.08)",
              marginBottom: 12,
              gap: 6,
            }}
          >
            <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase", color: COLORS.AMBER.hex }}>
              School awaiting verification
            </Text>
            <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 12.5, lineHeight: 18, color: "rgba(28,43,26,0.7)" }}>
              Coravu is reviewing your profile. This typically takes 1–2 business days. Some cases require verification before you can join.
            </Text>
            <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 11.5, color: "rgba(28,43,26,0.55)" }}>
              Need to expedite? Email{" "}
              <Text
                style={{ fontFamily: "DMSans_600SemiBold", color: COLORS.AMBER.hex }}
                onPress={() => {
                  if (Platform.OS === "web" && typeof window !== "undefined") {
                    window.location.href = "mailto:admin@coravu.com";
                  }
                }}
              >
                admin@coravu.com
              </Text>
              .
            </Text>
          </View>
        ) : student && student.school_verified === true ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              marginBottom: 12,
            }}
          >
            <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, color: "#3F5A30" }}>
              ✓ Verified: {university || "school on file"}
            </Text>
          </View>
        ) : null}

        {/* College stays locked — sourced from the email-domain match at
            signup. Major is editable, but only via a dropdown filtered
            to the majors offered at this user's college, so they can't
            sneak into a program that filters by major. */}
        <ReadOnlyField label="College" value={university} hint="From signup. Contact support to update." />
        <MajorPickerField
          college={university}
          value={major}
          onChange={setMajor}
        />
        <ChoiceField
          label="Program level"
          options={[...PROGRAM_LEVELS]}
          value={programLevel}
          onChange={setProgramLevel}
        />
        <View style={{ flexDirection: "row", gap: 12 }}>
          <View style={{ flex: 1 }}>
            <ChoiceField
              label="Graduation term"
              options={[...GRAD_TERMS]}
              value={gradTerm}
              onChange={setGradTerm}
            />
          </View>
          <View style={{ flex: 1 }}>
            {/* gradYear is a state-stored string (so empty = "not set"
                round-trips cleanly). The picker writes the chosen year
                back as a string; save() coerces to number. */}
            <ChoiceField
              label="Graduation year"
              options={gradYearOptions().map((y) => String(y))}
              value={gradYear}
              onChange={setGradYear}
            />
          </View>
        </View>
      </Field>

      {/* Contact */}
      <Field label="Contact">
        <LinkedinField value={linkedinUrl} onChange={setLinkedinUrl} />
        <TextField label="Phone" value={phone} onChange={setPhone} placeholder="(optional)" />
      </Field>

      {/* Resume */}
      <Field label="Resume">
        {resumeUrl ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              padding: 12,
              backgroundColor: "rgba(247,244,238,0.6)",
              borderWidth: 1,
              borderColor: "rgba(28,43,26,0.08)",
              borderRadius: 4,
            }}
          >
            <View
              style={{
                width: 36,
                height: 44,
                borderRadius: 3,
                backgroundColor: "rgba(181,135,58,0.15)",
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.32)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 9, color: AMBER, letterSpacing: 1 }}>
                PDF
              </Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 12.5,
                  color: FOREST,
                }}
                numberOfLines={1}
              >
                Resume on file
              </Text>
              {resumeSignedUrl ? (
                <Pressable onPress={() => Linking.openURL(resumeSignedUrl)}>
                  <Text
                    style={{
                      marginTop: 2,
                      fontFamily: "DMSans_500Medium",
                      fontSize: 11,
                      color: AMBER,
                      letterSpacing: 0.6,
                    }}
                  >
                    View →
                  </Text>
                </Pressable>
              ) : null}
            </View>
            <UploadButton
              label={uploadingResume ? "Uploading…" : "Replace"}
              onPress={onPickResume}
              disabled={uploadingResume}
            />
          </View>
        ) : (
          <UploadButton
            label={uploadingResume ? "Uploading…" : "Upload resume"}
            onPress={onPickResume}
            disabled={uploadingResume}
          />
        )}
      </Field>

      {/* Save bar */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          paddingTop: 8,
          borderTopWidth: 1,
          borderTopColor: "rgba(28,43,26,0.08)",
          flexWrap: "wrap",
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 11,
            color: errorMsg ? "#C0392B" : TEXT_FAINT,
          }}
        >
          {errorMsg ?? savedLabel}
        </Text>
        <Pressable
          onPress={() => void save()}
          disabled={saving}
          style={({ hovered }) => [
            {
              backgroundColor: saving ? "rgba(28,43,26,0.18)" : AMBER,
              paddingVertical: 11,
              paddingHorizontal: 22,
              borderRadius: 4,
            },
            Platform.OS === "web" && hovered && !saving ? { backgroundColor: COLORS.AMBER_WARM.hex } : {},
            Platform.OS === "web" ? ({ cursor: saving ? "not-allowed" : "pointer" } as unknown as ViewStyle) : {},
          ]}
        >
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: saving ? "rgba(255,255,255,0.7)" : COLORS.BLACK.hex,
            }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Text>
        </Pressable>
      </View>
    </ScrollView>
  );
}


/**
 * Brand-aligned toggle. Custom-built View + Pressable instead of the
 * platform Switch so we can match the Coravu palette exactly:
 *   - Off: muted forest track at 14% opacity, white thumb
 *   - On:  forest track, white thumb shifted right
 *   - Animated translateX on the thumb gives a smooth slide.
 */
function BrandToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  const isWeb = Platform.OS === "web";
  const trackW = 38;
  const trackH = 22;
  const thumbD = 16;
  const inset = 3;
  const trackColor = value ? COLORS.FOREST.hex : "rgba(28,43,26,0.18)";
  return (
    <Pressable
      onPress={() => onChange(!value)}
      accessibilityRole="switch"
      accessibilityState={{ checked: value }}
      style={[
        {
          width: trackW,
          height: trackH,
          borderRadius: trackH / 2,
          backgroundColor: trackColor,
          padding: inset,
          justifyContent: "center",
          borderWidth: 1,
          borderColor: value ? "rgba(181,135,58,0.55)" : "rgba(28,43,26,0.18)",
        },
        isWeb
          ? ({
              cursor: "pointer",
              transition:
                "background-color 0.18s ease, border-color 0.18s ease",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <View
        style={[
          {
            width: thumbD,
            height: thumbD,
            borderRadius: thumbD / 2,
            backgroundColor: COLORS.WHITE.hex,
            transform: [{ translateX: value ? trackW - thumbD - inset * 2 - 2 : 0 }],
            shadowColor: "#000",
            shadowOpacity: 0.18,
            shadowRadius: 3,
            shadowOffset: { width: 0, height: 1 },
          },
          isWeb
            ? ({ transition: "transform 0.18s ease" } as object)
            : null,
        ]}
      />
    </Pressable>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 9.5,
          letterSpacing: 1.8,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 10,
        }}
      >
        {label}
      </Text>
      <View style={{ gap: 10 }}>{children}</View>
    </View>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  keyboardType,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  keyboardType?: "default" | "number-pad";
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: "DMSans_500Medium",
          fontSize: 11,
          color: TEXT_MUTED,
          marginBottom: 4,
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Text>
      <TextInput
        value={value}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor={TEXT_FAINT}
        keyboardType={keyboardType ?? "default"}
        style={{
          backgroundColor: COLORS.WHITE.hex,
          borderWidth: 1,
          borderColor: SAND_DARK,
          borderRadius: 4,
          paddingHorizontal: 12,
          paddingVertical: 10,
          fontFamily: "DMSans_400Regular",
          fontSize: 13,
          color: FOREST,
          outlineWidth: 0 as unknown as number,
        }}
      />
    </View>
  );
}

/**
 * LinkedIn URL field with live validation. Shows a red inline message
 * the moment the user types something that isn't a LinkedIn URL — and
 * the save() guard refuses to persist invalid input, so even if the
 * user ignores the message and clicks Save, garbage like
 * "jsdjlkfjsdlfkjf" never reaches the database.
 */
function LinkedinField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const showError = value.trim().length > 0 && !isLinkedinUrlValid(value);
  return (
    <View>
      <Text
        style={{
          fontFamily: "DMSans_500Medium",
          fontSize: 11,
          color: TEXT_MUTED,
          marginBottom: 4,
          letterSpacing: 0.4,
        }}
      >
        LinkedIn URL
      </Text>
      <TextInput
        value={value}
        onChangeText={onChange}
        placeholder="https://www.linkedin.com/in/yourname"
        placeholderTextColor={TEXT_FAINT}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="url"
        style={{
          backgroundColor: COLORS.WHITE.hex,
          borderWidth: 1,
          borderColor: showError ? "#A33D2F" : SAND_DARK,
          borderRadius: 4,
          paddingHorizontal: 12,
          paddingVertical: 10,
          fontFamily: "DMSans_400Regular",
          fontSize: 13,
          color: FOREST,
          outlineWidth: 0 as unknown as number,
        }}
      />
      {showError ? (
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 11,
            color: "#A33D2F",
            marginTop: 4,
          }}
        >
          Must be a real LinkedIn URL — paste the full link from your profile.
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Major picker — searchable dropdown filtered to the majors offered at
 * the user's locked college. Built directly here (rather than reusing
 * the EligibilityPickers MultiSelect from the client portal) because
 * this is single-select, lives inside an HeaderOverlay panel, and
 * needs different sizing / type-to-filter UX than the eligibility one.
 */
function MajorPickerField({
  college,
  value,
  onChange,
}: {
  college: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const allMajors = useMemo(
    () => majorsForSchools(college ? [college] : []),
    [college],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allMajors;
    return allMajors.filter((m) => m.toLowerCase().includes(q));
  }, [allMajors, query]);

  return (
    <View>
      <Text
        style={{
          fontFamily: "DMSans_500Medium",
          fontSize: 11,
          color: TEXT_MUTED,
          marginBottom: 4,
          letterSpacing: 0.4,
        }}
      >
        Major
      </Text>
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel="Choose major"
        style={{
          backgroundColor: COLORS.WHITE.hex,
          borderWidth: 1,
          borderColor: SAND_DARK,
          borderRadius: 4,
          paddingHorizontal: 12,
          paddingVertical: 10,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 13,
            color: value ? FOREST : TEXT_FAINT,
            flex: 1,
          }}
          numberOfLines={1}
        >
          {value || "Select a major"}
        </Text>
        <Svg width={10} height={6} viewBox="0 0 10 6" fill="none">
          <Path d="M1 1 L5 5 L9 1" stroke={TEXT_MUTED} strokeWidth={1.4} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </Svg>
      </Pressable>

      <RNModal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          onPress={() => setOpen(false)}
          style={{
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.45)",
            alignItems: "center",
            justifyContent: "center",
            paddingHorizontal: 24,
          }}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 480,
              maxHeight: 520,
              backgroundColor: COLORS.LINEN.hex,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: SAND_DARK,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                paddingHorizontal: 16,
                paddingVertical: 12,
                borderBottomWidth: 1,
                borderBottomColor: SAND_DARK,
                gap: 8,
              }}
            >
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 10,
                  letterSpacing: 1.4,
                  color: AMBER,
                }}
              >
                {college ? "MAJORS AT " + college.toUpperCase() : "MAJORS"}
              </Text>
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Search majors…"
                placeholderTextColor={TEXT_FAINT}
                autoFocus={Platform.OS === "web"}
                style={{
                  backgroundColor: COLORS.WHITE.hex,
                  borderWidth: 1,
                  borderColor: SAND_DARK,
                  borderRadius: 4,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  fontFamily: "DMSans_400Regular",
                  fontSize: 13,
                  color: FOREST,
                  outlineWidth: 0 as unknown as number,
                }}
              />
            </View>
            <ScrollView style={{ maxHeight: 420 }}>
              {filtered.length === 0 ? (
                <Text
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 13,
                    color: TEXT_FAINT,
                    paddingHorizontal: 16,
                    paddingVertical: 14,
                  }}
                >
                  No majors match.
                </Text>
              ) : (
                filtered.map((m) => {
                  const isSelected = m === value;
                  return (
                    <Pressable
                      key={m}
                      onPress={() => {
                        onChange(m);
                        setOpen(false);
                        setQuery("");
                      }}
                      style={({ hovered }: { hovered?: boolean }) => [
                        {
                          paddingHorizontal: 16,
                          paddingVertical: 10,
                          borderBottomWidth: 0.5,
                          borderBottomColor: "rgba(28,43,26,0.10)",
                          backgroundColor: isSelected
                            ? "rgba(181,135,58,0.10)"
                            : hovered && Platform.OS === "web"
                            ? "rgba(28,43,26,0.04)"
                            : "transparent",
                        },
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <Text
                        style={{
                          fontFamily: isSelected ? "DMSans_600SemiBold" : "DMSans_400Regular",
                          fontSize: 13,
                          color: isSelected ? AMBER : FOREST,
                        }}
                      >
                        {m}
                      </Text>
                    </Pressable>
                  );
                })
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </RNModal>
    </View>
  );
}

/**
 * Locked, display-only field with a small padlock glyph and a hint line.
 */
function ReadOnlyField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <View>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 11,
            color: TEXT_MUTED,
            letterSpacing: 0.4,
          }}
        >
          {label}
        </Text>
        <Svg width={9} height={11} viewBox="0 0 9 11" fill="none">
          <Path
            d="M2 5 V3.2 a2.5 2.5 0 0 1 5 0 V5 M0.8 5 H8.2 V10 H0.8 Z"
            stroke={TEXT_MUTED}
            strokeWidth={1}
            fill="none"
            strokeLinejoin="round"
          />
        </Svg>
      </View>
      <View
        style={{
          backgroundColor: "rgba(28,43,26,0.04)",
          borderWidth: 1,
          borderColor: SAND_DARK,
          borderRadius: 4,
          paddingHorizontal: 12,
          paddingVertical: 10,
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 13,
            color: value ? FOREST : TEXT_FAINT,
          }}
          numberOfLines={1}
        >
          {value || "Not set"}
        </Text>
      </View>
      {hint ? (
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 10.5,
            color: TEXT_FAINT,
            marginTop: 4,
          }}
        >
          {hint}
        </Text>
      ) : null}
    </View>
  );
}

function ChoiceField({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: "DMSans_500Medium",
          fontSize: 11,
          color: TEXT_MUTED,
          marginBottom: 6,
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Text>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        {options.map((opt) => {
          const active = value === opt;
          return (
            <Pressable
              key={opt}
              onPress={() => onChange(active ? "" : opt)}
              style={[
                {
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 999,
                  borderWidth: 1,
                  backgroundColor: active ? "rgba(181,135,58,0.10)" : "transparent",
                  borderColor: active ? "rgba(181,135,58,0.4)" : "rgba(28,43,26,0.12)",
                },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {},
              ]}
            >
              <Text
                style={{
                  fontFamily: active ? "DMSans_600SemiBold" : "DMSans_500Medium",
                  fontSize: 12,
                  color: active ? AMBER : FOREST,
                }}
              >
                {opt}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

function UploadButton({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ hovered }) => [
        {
          paddingVertical: 8,
          paddingHorizontal: 14,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: disabled ? "rgba(28,43,26,0.10)" : "rgba(28,43,26,0.18)",
          backgroundColor: "transparent",
        },
        Platform.OS === "web" && hovered && !disabled
          ? { borderColor: AMBER, backgroundColor: "rgba(181,135,58,0.06)" }
          : {},
        Platform.OS === "web"
          ? ({ cursor: disabled ? "not-allowed" : "pointer" } as unknown as ViewStyle)
          : {},
      ]}
    >
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: disabled ? TEXT_FAINT : FOREST,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

// Helper hook kept for comp